export type SubgenreType = 'core' | 'crossover' | 'microgenre'

export interface SubgenreEntry {
  name: string
  type: SubgenreType
  /** Canonical, reader-facing label. Used everywhere by default. */
  displayLabel: string
  /**
   * Context-stripped label for chips shown INSIDE an already-scoped surface
   * (e.g. "Sci-Fi" on the Romance genre page, where "Romance" is implied).
   */
  shortLabel: string
  /**
   * Space-constrained label for surfaces where the parent genre is NOT implied
   * (book card spines, mobile tags). Unlike shortLabel this keeps the genre but
   * shortens it — "Sci-Fi Rom.". Falls back to displayLabel when omitted.
   */
  abbreviation?: string
  parentGenre: string
  secondaryGenres?: string[]
}

export interface GenreRegistry {
  genre: string
  subgenres: {
    core: SubgenreEntry[]
    crossover: SubgenreEntry[]
    microgenre: SubgenreEntry[]
  }
}

/**
 * CANONICAL_REGISTRIES is the single source of truth for the subgenre taxonomy.
 *
 * Rules:
 * - Every subgenre is declared EXACTLY ONCE, under its `parentGenre`.
 * - `displayLabel` is the canonical, reader-facing label used everywhere in the
 *   product (e.g. "Contemporary Romance", never a bare "Contemporary").
 * - `shortLabel` is the compact form for space-constrained UI (chips, badges,
 *   cards) where the parent genre is already implied by context.
 * - Crossover entries list their other parent(s) in `secondaryGenres`. They are
 *   NOT duplicated here — `SUBGENRE_REGISTRIES` below surfaces the very same
 *   entry object under each parent genre.
 */
const CANONICAL_REGISTRIES: Record<string, GenreRegistry> = {
  Romance: {
    genre: 'Romance',
    subgenres: {
      core: [
        {
          name: 'Contemporary Romance',
          type: 'core',
          displayLabel: 'Contemporary Romance',
          shortLabel: 'Contemporary',
          parentGenre: 'Romance',
        },
        {
          name: 'Historical Romance',
          type: 'core',
          displayLabel: 'Historical Romance',
          shortLabel: 'Historical',
          parentGenre: 'Romance',
        },
        {
          name: 'Romantic Comedy',
          type: 'core',
          displayLabel: 'Romantic Comedy',
          shortLabel: 'Rom-Com',
          parentGenre: 'Romance',
        },
        {
          name: 'Romantic Suspense',
          type: 'core',
          displayLabel: 'Romantic Suspense',
          shortLabel: 'Suspense',
          parentGenre: 'Romance',
        },
        {
          name: 'Dark Romance',
          type: 'core',
          displayLabel: 'Dark Romance',
          shortLabel: 'Dark',
          parentGenre: 'Romance',
        },
        {
          name: 'Erotic Romance',
          type: 'core',
          displayLabel: 'Erotic Romance',
          shortLabel: 'Erotic',
          parentGenre: 'Romance',
        },
        {
          name: 'New Adult Romance',
          type: 'core',
          displayLabel: 'New Adult Romance',
          shortLabel: 'New Adult',
          parentGenre: 'Romance',
        },
        // Promoted from microgenre: readers actively browse these by name, and
        // mainstream platforms collapse them into Dark Romance. Surfacing them
        // as core shelves is a deliberate point of difference.
        {
          name: 'Pitch Black Romance',
          type: 'core',
          displayLabel: 'Pitch Black Romance',
          shortLabel: 'Pitch Black',
          parentGenre: 'Romance',
        },
        {
          name: 'Taboo Romance',
          type: 'core',
          displayLabel: 'Taboo Romance',
          shortLabel: 'Taboo',
          parentGenre: 'Romance',
        },
        {
          name: 'Omegaverse',
          type: 'core',
          displayLabel: 'Omegaverse',
          shortLabel: 'Omegaverse',
          parentGenre: 'Romance',
        },
      ],
      crossover: [
        {
          name: 'Fantasy Romance',
          type: 'crossover',
          displayLabel: 'Fantasy Romance',
          shortLabel: 'Fantasy',
          parentGenre: 'Romance',
          secondaryGenres: ['Fantasy'],
        },
        {
          name: 'Sci-Fi Romance',
          type: 'crossover',
          displayLabel: 'Sci-Fi Romance',
          shortLabel: 'Sci-Fi',
          parentGenre: 'Romance',
          secondaryGenres: ['Science Fiction'],
        },
        {
          name: 'Paranormal Romance',
          type: 'crossover',
          displayLabel: 'Paranormal Romance',
          shortLabel: 'Paranormal',
          parentGenre: 'Romance',
          secondaryGenres: ['Fantasy'],
        },
        {
          name: 'Horror Romance',
          type: 'crossover',
          displayLabel: 'Horror Romance',
          shortLabel: 'Horror',
          parentGenre: 'Romance',
          secondaryGenres: ['Horror'],
        },
        {
          name: 'Horror Erotica',
          type: 'crossover',
          displayLabel: 'Horror Erotica',
          shortLabel: 'Horror Erotica',
          parentGenre: 'Romance',
          secondaryGenres: ['Horror'],
        },
        {
          name: 'Gothic Romance',
          type: 'crossover',
          displayLabel: 'Gothic Romance',
          shortLabel: 'Gothic',
          parentGenre: 'Romance',
          secondaryGenres: ['Horror'],
        },
      ],
      microgenre: [
        {
          name: 'Small Town Romance',
          type: 'microgenre',
          displayLabel: 'Small Town Romance',
          shortLabel: 'Small Town',
          parentGenre: 'Romance',
        },
        {
          name: 'Sports Romance',
          type: 'microgenre',
          displayLabel: 'Sports Romance',
          shortLabel: 'Sports',
          parentGenre: 'Romance',
        },
        {
          name: 'Billionaire Romance',
          type: 'microgenre',
          displayLabel: 'Billionaire Romance',
          shortLabel: 'Billionaire',
          parentGenre: 'Romance',
        },
        {
          name: 'Mafia Romance',
          type: 'microgenre',
          displayLabel: 'Mafia Romance',
          shortLabel: 'Mafia',
          parentGenre: 'Romance',
        },
        {
          name: 'MC Romance',
          type: 'microgenre',
          displayLabel: 'MC Romance',
          shortLabel: 'MC',
          parentGenre: 'Romance',
        },
        {
          name: 'Military Romance',
          type: 'microgenre',
          displayLabel: 'Military Romance',
          shortLabel: 'Military',
          parentGenre: 'Romance',
        },
        {
          name: 'Workplace Romance',
          type: 'microgenre',
          displayLabel: 'Workplace Romance',
          shortLabel: 'Workplace',
          parentGenre: 'Romance',
        },
        {
          name: 'College Romance',
          type: 'microgenre',
          displayLabel: 'College Romance',
          shortLabel: 'College',
          parentGenre: 'Romance',
        },
        {
          name: 'Monster Romance',
          type: 'microgenre',
          displayLabel: 'Monster Romance',
          shortLabel: 'Monster',
          parentGenre: 'Romance',
        },
        {
          name: 'Alien Romance',
          type: 'microgenre',
          displayLabel: 'Alien Romance',
          shortLabel: 'Alien',
          parentGenre: 'Romance',
        },
        {
          name: 'Stalker Romance',
          type: 'microgenre',
          displayLabel: 'Stalker Romance',
          shortLabel: 'Stalker',
          parentGenre: 'Romance',
        },
        {
          name: 'Captive Romance',
          type: 'microgenre',
          displayLabel: 'Captive Romance',
          shortLabel: 'Captive',
          parentGenre: 'Romance',
        },
        {
          name: 'Psychological Romance',
          type: 'microgenre',
          displayLabel: 'Psychological Romance',
          shortLabel: 'Psychological',
          parentGenre: 'Romance',
        },
      ],
    },
  },
  Fantasy: {
    genre: 'Fantasy',
    subgenres: {
      core: [
        {
          name: 'Epic Fantasy',
          type: 'core',
          displayLabel: 'Epic Fantasy',
          shortLabel: 'Epic',
          parentGenre: 'Fantasy',
        },
        {
          name: 'High Fantasy',
          type: 'core',
          displayLabel: 'High Fantasy',
          shortLabel: 'High',
          parentGenre: 'Fantasy',
        },
        {
          name: 'Low Fantasy',
          type: 'core',
          displayLabel: 'Low Fantasy',
          shortLabel: 'Low',
          parentGenre: 'Fantasy',
        },
        {
          name: 'Urban Fantasy',
          type: 'core',
          displayLabel: 'Urban Fantasy',
          shortLabel: 'Urban',
          parentGenre: 'Fantasy',
        },
        {
          name: 'Dark Fantasy',
          type: 'core',
          displayLabel: 'Dark Fantasy',
          shortLabel: 'Dark',
          parentGenre: 'Fantasy',
        },
        {
          name: 'Cozy Fantasy',
          type: 'core',
          displayLabel: 'Cozy Fantasy',
          shortLabel: 'Cozy',
          parentGenre: 'Fantasy',
        },
        {
          name: 'Sword & Sorcery',
          type: 'core',
          displayLabel: 'Sword & Sorcery',
          shortLabel: 'Sword & Sorcery',
          parentGenre: 'Fantasy',
        },
        {
          name: 'Mythic Fantasy',
          type: 'core',
          displayLabel: 'Mythic Fantasy',
          shortLabel: 'Mythic',
          parentGenre: 'Fantasy',
        },
        {
          name: 'YA Fantasy',
          type: 'core',
          displayLabel: 'YA Fantasy',
          shortLabel: 'YA',
          parentGenre: 'Fantasy',
        },
      ],
      crossover: [
        {
          name: 'Historical Fantasy',
          type: 'crossover',
          displayLabel: 'Historical Fantasy',
          shortLabel: 'Historical',
          parentGenre: 'Fantasy',
          secondaryGenres: ['Historical Fiction'],
        },
      ],
      microgenre: [
        {
          name: 'Grimdark',
          type: 'microgenre',
          displayLabel: 'Grimdark',
          shortLabel: 'Grimdark',
          parentGenre: 'Fantasy',
        },
        {
          name: 'Progression Fantasy',
          type: 'microgenre',
          displayLabel: 'Progression Fantasy',
          shortLabel: 'Progression',
          parentGenre: 'Fantasy',
        },
        {
          name: 'LitRPG',
          type: 'microgenre',
          displayLabel: 'LitRPG',
          shortLabel: 'LitRPG',
          parentGenre: 'Fantasy',
        },
        {
          name: 'Portal Fantasy',
          type: 'microgenre',
          displayLabel: 'Portal Fantasy',
          shortLabel: 'Portal',
          parentGenre: 'Fantasy',
        },
        {
          name: 'Gaslamp Fantasy',
          type: 'microgenre',
          displayLabel: 'Gaslamp Fantasy',
          shortLabel: 'Gaslamp',
          parentGenre: 'Fantasy',
        },
      ],
    },
  },
  Horror: {
    genre: 'Horror',
    subgenres: {
      core: [
        {
          name: 'Psychological Horror',
          type: 'core',
          displayLabel: 'Psychological Horror',
          shortLabel: 'Psychological',
          parentGenre: 'Horror',
        },
        {
          name: 'Supernatural Horror',
          type: 'core',
          displayLabel: 'Supernatural Horror',
          shortLabel: 'Supernatural',
          parentGenre: 'Horror',
        },
        {
          name: 'Cosmic Horror',
          type: 'core',
          displayLabel: 'Cosmic Horror',
          shortLabel: 'Cosmic',
          parentGenre: 'Horror',
        },
        {
          name: 'Folk Horror',
          type: 'core',
          displayLabel: 'Folk Horror',
          shortLabel: 'Folk',
          parentGenre: 'Horror',
        },
        {
          name: 'Gothic Horror',
          type: 'core',
          displayLabel: 'Gothic Horror',
          shortLabel: 'Gothic',
          parentGenre: 'Horror',
        },
        {
          name: 'Body Horror',
          type: 'core',
          displayLabel: 'Body Horror',
          shortLabel: 'Body',
          parentGenre: 'Horror',
        },
      ],
      crossover: [],
      microgenre: [
        {
          name: 'Creature Horror',
          type: 'microgenre',
          displayLabel: 'Creature Horror',
          shortLabel: 'Creature',
          parentGenre: 'Horror',
        },
        {
          name: 'Haunted House',
          type: 'microgenre',
          displayLabel: 'Haunted House',
          shortLabel: 'Haunted House',
          parentGenre: 'Horror',
        },
        {
          name: 'Survival Horror',
          type: 'microgenre',
          displayLabel: 'Survival Horror',
          shortLabel: 'Survival',
          parentGenre: 'Horror',
        },
        {
          name: 'Extreme Horror',
          type: 'microgenre',
          displayLabel: 'Extreme Horror',
          shortLabel: 'Extreme',
          parentGenre: 'Horror',
        },
        {
          name: 'Splatterpunk',
          type: 'microgenre',
          displayLabel: 'Splatterpunk',
          shortLabel: 'Splatterpunk',
          parentGenre: 'Horror',
        },
        {
          name: 'Occult Horror',
          type: 'microgenre',
          displayLabel: 'Occult Horror',
          shortLabel: 'Occult',
          parentGenre: 'Horror',
        },
        {
          name: 'Slasher',
          type: 'microgenre',
          displayLabel: 'Slasher',
          shortLabel: 'Slasher',
          parentGenre: 'Horror',
        },
      ],
    },
  },
  Thriller: {
    genre: 'Thriller',
    subgenres: {
      core: [
        {
          name: 'Psychological Thriller',
          type: 'core',
          displayLabel: 'Psychological Thriller',
          shortLabel: 'Psychological',
          parentGenre: 'Thriller',
        },
        {
          name: 'Domestic Thriller',
          type: 'core',
          displayLabel: 'Domestic Thriller',
          shortLabel: 'Domestic',
          parentGenre: 'Thriller',
        },
        {
          name: 'Crime Thriller',
          type: 'core',
          displayLabel: 'Crime Thriller',
          shortLabel: 'Crime',
          parentGenre: 'Thriller',
        },
        {
          name: 'Spy Thriller',
          type: 'core',
          displayLabel: 'Spy Thriller',
          shortLabel: 'Spy',
          parentGenre: 'Thriller',
        },
        {
          name: 'Techno Thriller',
          type: 'core',
          displayLabel: 'Techno Thriller',
          shortLabel: 'Techno',
          parentGenre: 'Thriller',
        },
        {
          name: 'Medical Thriller',
          type: 'core',
          displayLabel: 'Medical Thriller',
          shortLabel: 'Medical',
          parentGenre: 'Thriller',
        },
        {
          name: 'Legal Thriller',
          type: 'core',
          displayLabel: 'Legal Thriller',
          shortLabel: 'Legal',
          parentGenre: 'Thriller',
        },
        {
          name: 'Political Thriller',
          type: 'core',
          displayLabel: 'Political Thriller',
          shortLabel: 'Political',
          parentGenre: 'Thriller',
        },
      ],
      crossover: [
        {
          name: 'Supernatural Thriller',
          type: 'crossover',
          displayLabel: 'Supernatural Thriller',
          shortLabel: 'Supernatural',
          parentGenre: 'Thriller',
          secondaryGenres: ['Horror'],
        },
      ],
      microgenre: [
        {
          name: 'Survival Thriller',
          type: 'microgenre',
          displayLabel: 'Survival Thriller',
          shortLabel: 'Survival',
          parentGenre: 'Thriller',
        },
      ],
    },
  },
  'Science Fiction': {
    genre: 'Science Fiction',
    subgenres: {
      core: [
        {
          name: 'Space Opera',
          type: 'core',
          displayLabel: 'Space Opera',
          shortLabel: 'Space Opera',
          parentGenre: 'Science Fiction',
        },
        {
          name: 'Cyberpunk',
          type: 'core',
          displayLabel: 'Cyberpunk',
          shortLabel: 'Cyberpunk',
          parentGenre: 'Science Fiction',
        },
        {
          name: 'Military Sci-Fi',
          type: 'core',
          displayLabel: 'Military Sci-Fi',
          shortLabel: 'Military',
          parentGenre: 'Science Fiction',
        },
        {
          name: 'Hard Sci-Fi',
          type: 'core',
          displayLabel: 'Hard Sci-Fi',
          shortLabel: 'Hard SF',
          parentGenre: 'Science Fiction',
        },
        {
          name: 'Dystopian Sci-Fi',
          type: 'core',
          displayLabel: 'Dystopian Sci-Fi',
          shortLabel: 'Dystopian',
          parentGenre: 'Science Fiction',
        },
        {
          name: 'Post-Apocalyptic',
          type: 'core',
          displayLabel: 'Post-Apocalyptic',
          shortLabel: 'Post-Apoc',
          parentGenre: 'Science Fiction',
        },
        {
          name: 'Time Travel',
          type: 'core',
          displayLabel: 'Time Travel',
          shortLabel: 'Time Travel',
          parentGenre: 'Science Fiction',
        },
      ],
      crossover: [
        {
          name: 'Sci-Fi Thriller',
          type: 'crossover',
          displayLabel: 'Sci-Fi Thriller',
          shortLabel: 'SF Thriller',
          parentGenre: 'Science Fiction',
          secondaryGenres: ['Thriller'],
        },
      ],
      microgenre: [
        {
          name: 'First Contact',
          type: 'microgenre',
          displayLabel: 'First Contact',
          shortLabel: 'First Contact',
          parentGenre: 'Science Fiction',
        },
        {
          name: 'Climate Fiction',
          type: 'microgenre',
          displayLabel: 'Climate Fiction',
          shortLabel: 'Cli-Fi',
          parentGenre: 'Science Fiction',
        },
        {
          name: 'Biopunk',
          type: 'microgenre',
          displayLabel: 'Biopunk',
          shortLabel: 'Biopunk',
          parentGenre: 'Science Fiction',
        },
      ],
    },
  },
  Mystery: {
    genre: 'Mystery',
    subgenres: {
      core: [
        {
          name: 'Cozy Mystery',
          type: 'core',
          displayLabel: 'Cozy Mystery',
          shortLabel: 'Cozy',
          parentGenre: 'Mystery',
        },
        {
          name: 'Police Procedural',
          type: 'core',
          displayLabel: 'Police Procedural',
          shortLabel: 'Procedural',
          parentGenre: 'Mystery',
        },
        {
          name: 'Amateur Sleuth',
          type: 'core',
          displayLabel: 'Amateur Sleuth',
          shortLabel: 'Amateur Sleuth',
          parentGenre: 'Mystery',
        },
        {
          name: 'Hard-Boiled Mystery',
          type: 'core',
          displayLabel: 'Hard-Boiled Mystery',
          shortLabel: 'Hard-Boiled',
          parentGenre: 'Mystery',
        },
        {
          name: 'Historical Mystery',
          type: 'core',
          displayLabel: 'Historical Mystery',
          shortLabel: 'Historical',
          parentGenre: 'Mystery',
        },
        {
          name: 'Noir',
          type: 'core',
          displayLabel: 'Noir',
          shortLabel: 'Noir',
          parentGenre: 'Mystery',
        },
      ],
      crossover: [
        {
          name: 'Literary Mystery',
          type: 'crossover',
          displayLabel: 'Literary Mystery',
          shortLabel: 'Literary',
          parentGenre: 'Mystery',
          secondaryGenres: ['Literary Fiction'],
        },
        {
          name: 'Supernatural Mystery',
          type: 'crossover',
          displayLabel: 'Supernatural Mystery',
          shortLabel: 'Supernatural',
          parentGenre: 'Mystery',
          secondaryGenres: ['Horror'],
        },
      ],
      microgenre: [],
    },
  },
  'Historical Fiction': {
    genre: 'Historical Fiction',
    subgenres: {
      core: [
        {
          name: 'Historical Literary',
          type: 'core',
          displayLabel: 'Historical Literary',
          shortLabel: 'Literary',
          parentGenre: 'Historical Fiction',
        },
        {
          name: 'Historical Adventure',
          type: 'core',
          displayLabel: 'Historical Adventure',
          shortLabel: 'Adventure',
          parentGenre: 'Historical Fiction',
        },
      ],
      crossover: [],
      microgenre: [],
    },
  },
  Adventure: {
    genre: 'Adventure',
    subgenres: {
      core: [
        {
          name: 'Action Adventure',
          type: 'core',
          displayLabel: 'Action Adventure',
          shortLabel: 'Action',
          parentGenre: 'Adventure',
        },
        {
          name: 'Coming-of-Age Adventure',
          type: 'core',
          displayLabel: 'Coming-of-Age Adventure',
          shortLabel: 'Coming-of-Age',
          parentGenre: 'Adventure',
        },
      ],
      crossover: [],
      microgenre: [],
    },
  },
  'Contemporary Fiction': {
    genre: 'Contemporary Fiction',
    subgenres: {
      core: [
        {
          name: 'Family Drama',
          type: 'core',
          displayLabel: 'Family Drama',
          shortLabel: 'Family Drama',
          parentGenre: 'Contemporary Fiction',
        },
        {
          name: 'Coming of Age',
          type: 'core',
          displayLabel: 'Coming of Age',
          shortLabel: 'Coming of Age',
          parentGenre: 'Contemporary Fiction',
        },
        {
          name: "Women's Fiction",
          type: 'core',
          displayLabel: "Women's Fiction",
          shortLabel: "Women's",
          parentGenre: 'Contemporary Fiction',
        },
        {
          name: 'Small Town Fiction',
          type: 'core',
          displayLabel: 'Small Town Fiction',
          shortLabel: 'Small Town',
          parentGenre: 'Contemporary Fiction',
        },
        {
          name: 'Workplace Fiction',
          type: 'core',
          displayLabel: 'Workplace Fiction',
          shortLabel: 'Workplace',
          parentGenre: 'Contemporary Fiction',
        },
        {
          name: 'Social Fiction',
          type: 'core',
          displayLabel: 'Social Fiction',
          shortLabel: 'Social',
          parentGenre: 'Contemporary Fiction',
        },
      ],
      crossover: [],
      microgenre: [],
    },
  },
  'Literary Fiction': {
    genre: 'Literary Fiction',
    subgenres: {
      core: [
        {
          name: 'Experimental Fiction',
          type: 'core',
          displayLabel: 'Experimental Fiction',
          shortLabel: 'Experimental',
          parentGenre: 'Literary Fiction',
        },
        {
          name: 'Autofiction',
          type: 'core',
          displayLabel: 'Autofiction',
          shortLabel: 'Autofiction',
          parentGenre: 'Literary Fiction',
        },
        {
          name: 'Postmodern Fiction',
          type: 'core',
          displayLabel: 'Postmodern Fiction',
          shortLabel: 'Postmodern',
          parentGenre: 'Literary Fiction',
        },
        {
          name: 'Magical Realism',
          type: 'core',
          displayLabel: 'Magical Realism',
          shortLabel: 'Magical Realism',
          parentGenre: 'Literary Fiction',
        },
      ],
      crossover: [],
      microgenre: [],
    },
  },
  'General Fiction': {
    genre: 'General Fiction',
    subgenres: {
      core: [
        {
          name: 'Book Club Fiction',
          type: 'core',
          displayLabel: 'Book Club Fiction',
          shortLabel: 'Book Club',
          parentGenre: 'General Fiction',
        },
        {
          name: 'Upmarket Fiction',
          type: 'core',
          displayLabel: 'Upmarket Fiction',
          shortLabel: 'Upmarket',
          parentGenre: 'General Fiction',
        },
        {
          name: 'Commercial Fiction',
          type: 'core',
          displayLabel: 'Commercial Fiction',
          shortLabel: 'Commercial',
          parentGenre: 'General Fiction',
        },
      ],
      crossover: [],
      microgenre: [],
    },
  },
}

/**
 * Builds the resolved registry: every genre gets its own subgenres PLUS the
 * crossover entries that name it in `secondaryGenres`. The inherited entries are
 * the SAME object references as the canonical ones — a crossover such as
 * "Horror Romance" is one record that surfaces under both Romance and Horror.
 */
const buildResolvedRegistries = (): Record<string, GenreRegistry> => {
  const resolved: Record<string, GenreRegistry> = {}
  for (const [genre, registry] of Object.entries(CANONICAL_REGISTRIES)) {
    resolved[genre] = {
      genre,
      subgenres: {
        core: [...registry.subgenres.core],
        crossover: [...registry.subgenres.crossover],
        microgenre: [...registry.subgenres.microgenre],
      },
    }
  }
  for (const registry of Object.values(CANONICAL_REGISTRIES)) {
    for (const entry of registry.subgenres.crossover) {
      for (const secondary of entry.secondaryGenres || []) {
        if (!resolved[secondary]) {
          resolved[secondary] = {
            genre: secondary,
            subgenres: { core: [], crossover: [], microgenre: [] },
          }
        }
        if (!resolved[secondary].subgenres.crossover.includes(entry)) {
          resolved[secondary].subgenres.crossover.push(entry)
        }
      }
    }
  }
  return resolved
}

export const SUBGENRE_REGISTRIES: Record<string, GenreRegistry> =
  buildResolvedRegistries()

export const SUBGENRE_TYPE_LABELS: Record<SubgenreType, string> = {
  core: 'Core',
  crossover: 'Crossover',
  microgenre: 'Microgenre',
}

export const SUBGENRE_TYPE_ORDER: SubgenreType[] = [
  'core',
  'crossover',
  'microgenre',
]

/**
 * PRESENTATION tiers. The Microgenre tier has been retired from the reader-
 * facing UI: the Genre → Subgenre → Content Tag drill-down now does that job
 * better, since "Hockey" under Sports Romance is a content tag, not a shelf.
 *
 * The underlying `microgenre` type is retained on entries (it still records the
 * breadth distinction, and vendor-side surfaces read it), but for display those
 * entries are folded into Core. See `getGroupedSubgenres`.
 */
export const SUBGENRE_TIER_ORDER: Extract<
  SubgenreType,
  'core' | 'crossover'
>[] = ['core', 'crossover']

/** URL-safe slug for a subgenre or genre label. */
export function toTaxonomySlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export function getSubgenresForGenre(genre: string): GenreRegistry | undefined {
  return SUBGENRE_REGISTRIES[genre]
}

/** Every subgenre entry for a genre, ordered core -> crossover -> microgenre. */
export function getAllSubgenreEntries(genre: string): SubgenreEntry[] {
  const registry = SUBGENRE_REGISTRIES[genre]
  if (!registry) return []
  return SUBGENRE_TYPE_ORDER.flatMap((type) => registry.subgenres[type])
}

/** Canonical display labels for a genre — the derived flat option list. */
export function getSubgenreOptions(genre: string): string[] {
  return getAllSubgenreEntries(genre).map((entry) => entry.displayLabel)
}

/**
 * Derived flat option map. This replaces the previously hand-maintained
 * `subGenreOptions` / `SUBGENRE_OPTIONS` lists so there is only one place to
 * edit the taxonomy.
 */
export const SUBGENRE_OPTIONS: Record<string, string[]> = Object.fromEntries(
  Object.keys(SUBGENRE_REGISTRIES).map((genre) => [
    genre,
    getSubgenreOptions(genre),
  ]),
)

/**
 * Legacy labels kept ONLY as read-side fallbacks for user-authored or
 * externally-sourced strings. The project's own seed data has been normalised
 * to canonical labels, so nothing in-repo depends on these.
 */
const LEGACY_LABEL_ALIASES: Record<string, string> = {
  'Science Fiction Romance': 'Sci-Fi Romance',
  'Erotic Horror': 'Horror Erotica',
  'Dystopian Science Fiction': 'Dystopian Sci-Fi',
  Dystopian: 'Dystopian Sci-Fi',
  'Young Adult Fantasy': 'YA Fantasy',
  'Literary Historical Fiction': 'Historical Literary',
  'Sword and Sorcery': 'Sword & Sorcery',
  'Climate Sci-Fi': 'Climate Fiction',
  Romantasy: 'Fantasy Romance',
}

const ENTRY_LOOKUP: Record<string, SubgenreEntry> = (() => {
  const lookup: Record<string, SubgenreEntry> = {}
  for (const registry of Object.values(CANONICAL_REGISTRIES)) {
    for (const type of SUBGENRE_TYPE_ORDER) {
      for (const entry of registry.subgenres[type]) {
        for (const key of [entry.name, entry.displayLabel, entry.shortLabel]) {
          const normalized = key.toLowerCase()
          if (!lookup[normalized]) lookup[normalized] = entry
        }
      }
    }
  }
  for (const [legacy, canonical] of Object.entries(LEGACY_LABEL_ALIASES)) {
    const entry = lookup[canonical.toLowerCase()]
    if (entry) lookup[legacy.toLowerCase()] = entry
  }
  return lookup
})()

/**
 * Resolves an entry from its canonical name, display label, short label or a
 * legacy label. Pass `genre` to disambiguate short labels shared across genres
 * (e.g. "Dark" is both Dark Romance and Dark Fantasy).
 */
export function getSubgenreEntry(
  mainSubgenre: string,
  genre?: string,
): SubgenreEntry | undefined {
  if (!mainSubgenre) return undefined
  const normalized = mainSubgenre.trim().toLowerCase()
  if (genre) {
    const scoped = getAllSubgenreEntries(genre).find((entry) =>
      [entry.name, entry.displayLabel, entry.shortLabel].some(
        (label) => label.toLowerCase() === normalized,
      ),
    )
    if (scoped) return scoped
  }
  return ENTRY_LOOKUP[normalized]
}

/**
 * Abbreviations for compact, unscoped surfaces (card spines, mobile tags).
 * Kept here so a subgenre still has ONE home, even though it carries three
 * label forms for three different display contexts.
 */
const SUBGENRE_ABBREVIATIONS: Record<string, string> = {
  'Sci-Fi Romance': 'Sci-Fi Rom.',
  'Sci-Fi Thriller': 'Sci-Fi Thriller',
  'Fantasy Romance': 'Fant. Romance',
  'Epic Fantasy': 'Epic Fant.',
  'Dark Fantasy': 'Dark Fant.',
  'High Fantasy': 'High Fant.',
  'Low Fantasy': 'Low Fant.',
  'Urban Fantasy': 'Urban Fant.',
  'Contemporary Romance': 'Cont. Romance',
  'Paranormal Romance': 'Para. Romance',
  'Historical Romance': 'Hist. Romance',
  'Romantic Suspense': 'Rom. Suspense',
  'Erotic Romance': 'Erotic Rom.',
  'Monster Romance': 'Monster Rom.',
  'Small Town Romance': 'Small Town',
  'Alien Romance': 'Alien Rom.',
  'Horror Romance': 'Horror Rom.',
  'Sports Romance': 'Sports Rom.',
  'Billionaire Romance': 'Billionaire Rom.',
  'Workplace Romance': 'Workplace Rom.',
  'Military Romance': 'Military Rom.',
  'College Romance': 'College Rom.',
  'New Adult Romance': 'New Adult Rom.',
  'Pitch Black Romance': 'Pitch Black Rom.',
  'Psychological Romance': 'Psych. Romance',
  'Supernatural Thriller': 'Supernatural',
  'Psychological Thriller': 'Psych. Thriller',
  'Political Thriller': 'Polit. Thriller',
  'Literary Mystery': 'Lit. Mystery',
  'Hard-Boiled Mystery': 'Hard-Boiled',
  'Historical Mystery': 'Hist. Mystery',
  'Historical Adventure': 'Hist. Adventure',
  'Historical Fantasy': 'Hist. Fantasy',
  'Historical Literary': 'Hist. Literary',
  'YA Fantasy': 'YA Fantasy',
  'Supernatural Horror': 'Supernatural',
  'Gothic Horror': 'Gothic',
  'Psychological Horror': 'Psych. Horror',
  'Dystopian Sci-Fi': 'Dystopian Sci-Fi',
  'Post-Apocalyptic': 'Post-Apoc.',
  'Military Sci-Fi': 'Military SF',
  'Mythic Fantasy': 'Mythic',
  'Progression Fantasy': 'Progression',
  'Coming-of-Age Adventure': 'CoA Adventure',
  "Women's Fiction": "Women's Fic.",
  'Experimental Fiction': 'Experimental',
  'Postmodern Fiction': 'Postmodern',
  'Book Club Fiction': 'Book Club',
  'Commercial Fiction': 'Commercial',
  'Upmarket Fiction': 'Upmarket',
}

/**
 * Compact label for surfaces where the parent genre is NOT already implied.
 * Falls back to the canonical display label, then the raw input.
 */
export function getSubgenreAbbreviation(
  subgenre: string,
  genre?: string,
): string {
  const entry = getSubgenreEntry(subgenre, genre)
  if (!entry) return subgenre
  return (
    entry.abbreviation ||
    SUBGENRE_ABBREVIATIONS[entry.displayLabel] ||
    entry.displayLabel
  )
}

/** Compact label for chips/badges, falling back to the input when unknown. */
export function getSubgenreShortLabel(subgenre: string, genre?: string): string {
  return getSubgenreEntry(subgenre, genre)?.shortLabel || subgenre
}

/** Canonical display label, used to normalise any legacy/short label. */
export function getSubgenreDisplayLabel(
  subgenre: string,
  genre?: string,
): string {
  return getSubgenreEntry(subgenre, genre)?.displayLabel || subgenre
}

export interface SubgenreGroup {
  type: SubgenreType
  label: string
  entries: SubgenreEntry[]
}

/** Subgenres for a genre bucketed into every raw tier, including Microgenre. */
export function getGroupedSubgenres(genre: string): SubgenreGroup[] {
  const registry = SUBGENRE_REGISTRIES[genre]
  if (!registry) return []
  return SUBGENRE_TYPE_ORDER.map((type) => ({
    type,
    label: SUBGENRE_TYPE_LABELS[type],
    entries: registry.subgenres[type],
  })).filter((group) => group.entries.length > 0)
}

/**
 * READER-FACING tiers: Core and Crossover only. Microgenre entries are merged
 * into Core, keeping their canonical order, so genre pages present two clean
 * groups instead of three. Use this for any public browse surface.
 */
export function getReaderSubgenreTiers(genre: string): SubgenreGroup[] {
  const registry = SUBGENRE_REGISTRIES[genre]
  if (!registry) return []
  const core = [...registry.subgenres.core, ...registry.subgenres.microgenre]
  return [
    { type: 'core' as SubgenreType, label: 'Core Subgenres', entries: core },
    {
      type: 'crossover' as SubgenreType,
      label: 'Crossover Subgenres',
      entries: registry.subgenres.crossover,
    },
  ].filter((group) => group.entries.length > 0)
}

/** Resolves a subgenre entry (and its parent genre) from a URL slug. */
export function getSubgenreBySlug(
  slug: string,
): { entry: SubgenreEntry; genre: string } | undefined {
  for (const [genre, registry] of Object.entries(SUBGENRE_REGISTRIES)) {
    for (const type of SUBGENRE_TYPE_ORDER) {
      for (const entry of registry.subgenres[type]) {
        if (toTaxonomySlug(entry.displayLabel) === slug) {
          // Prefer the entry's own parent so crossovers report one canonical home.
          return { entry, genre: entry.parentGenre || genre }
        }
      }
    }
  }
  return undefined
}
